import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Row, Col, Alert, Spinner } from 'react-bootstrap';
import API_URL from '../../config';
import ImovelSelector from '../Imovel/ImovelSelector';

function CalculatorForm() {
    const [imovelData, setImovelData] = useState(null); // { codigo, nome, isNew }
    const [benfeitoriaType, setBenfeitoriaType] = useState('');
    const [propertyStandard, setPropertyStandard] = useState('');
    const [estadoConservacao, setEstadoConservacao] = useState('');
    const [area, setArea] = useState('');
    const [apparentAge, setApparentAge] = useState('');

    const [benfeitoriaTypes, setBenfeitoriaTypes] = useState([]);
    const [propertyStandards, setPropertyStandards] = useState([]);

    const [calculationResult, setCalculationResult] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const pendingStandardRef = useRef(null);

    // ── Persistência de estado via sessionStorage ─────────────────────────
    useEffect(() => {
        const saved = sessionStorage.getItem('calc_form_state');
        if (saved) {
            try {
                const s = JSON.parse(saved);
                if (s.imovelData) setImovelData(s.imovelData);
                if (s.benfeitoriaType) { pendingStandardRef.current = s.propertyStandard || null; setBenfeitoriaType(s.benfeitoriaType); }
                if (s.estadoConservacao) setEstadoConservacao(s.estadoConservacao);
                if (s.area) setArea(s.area);
                if (s.apparentAge) setApparentAge(s.apparentAge);
            } catch {}
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const data = { imovelData, benfeitoriaType, propertyStandard, estadoConservacao, area, apparentAge };
        sessionStorage.setItem('calc_form_state', JSON.stringify(data));
    }, [imovelData, benfeitoriaType, propertyStandard, estadoConservacao, area, apparentAge]);

    const estadosConservacao = [
        { value: 'A', label: 'A — Nova (0,00%)' },
        { value: 'B', label: 'B — Entre nova e regular (0,32%)' },
        { value: 'C', label: 'C — Regular (2,52%)' },
        { value: 'D', label: 'D — Entre regular e reparos simples (8,09%)' },
        { value: 'E', label: 'E — Reparos simples (18,10%)' },
        { value: 'F', label: 'F — Reparos simples a importantes (33,20%)' },
        { value: 'G', label: 'G — Reparos importantes (52,60%)' },
        { value: 'H', label: 'H — Reparos importantes a sem valor (75,20%)' },
        { value: 'I', label: 'I — Sem valor (100,00%)' }
    ];

    useEffect(() => {
        axios.get(`${API_URL}/api/benfeitorias`, { withCredentials: true })
            .then(r => setBenfeitoriaTypes(r.data))
            .catch(() => setError('Erro ao carregar tipos de benfeitoria.'));
    }, []);

    useEffect(() => {
        if (benfeitoriaType) {
            axios.get(`${API_URL}/api/standards/${benfeitoriaType}`, { withCredentials: true })
                .then(r => {
                    setPropertyStandards(r.data);
                    if (pendingStandardRef.current) {
                        setPropertyStandard(pendingStandardRef.current);
                        pendingStandardRef.current = null;
                    } else {
                        setPropertyStandard('');
                    }
                })
                .catch(() => setError('Erro ao carregar padrões.'));
        } else {
            setPropertyStandards([]);
            setPropertyStandard('');
        }
    }, [benfeitoriaType]);

    const handleCalculate = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/calculate`, {
                benfeitoria_type: benfeitoriaType,
                property_standard: propertyStandard,
                area: parseFloat(area),
                apparent_age: parseInt(apparentAge, 10),
                estado_conservacao: estadoConservacao,
                nome_imovel: imovelData?.nome || ''
            }, { withCredentials: true });
            setCalculationResult(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao realizar o cálculo. Verifique os dados.');
            setCalculationResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCalculation = async () => {
        if (!calculationResult) return;
        setSaving(true);
        setError('');
        setMessage('');
        try {
            await axios.post(`${API_URL}/calculations`, {
                benfeitoria_type: benfeitoriaType,
                property_class: 'N/A',
                property_standard: propertyStandard,
                area: parseFloat(area),
                apparent_age: parseInt(apparentAge, 10),
                estado_conservacao: estadoConservacao,
                imovel_codigo: imovelData?.codigo || '',
                imovel_nome: imovelData?.nome || '',
                nome_imovel: imovelData?.nome || '',
                ...calculationResult
            }, { withCredentials: true });
            setMessage('Cálculo salvo com sucesso no histórico!');
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar o cálculo.');
        } finally {
            setSaving(false);
        }
    };

    const fmt = (n, dec = 2) => n?.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec }) ?? '—';

    return (
        <div className="bc-page">
            <div className="container" style={{ maxWidth: '820px' }}>

                <div className="bc-page-header">
                    <h2>Calculadora de Benfeitoria</h2>
                    <p>Avaliação de depreciação pelo método Ross-Heidecke</p>
                </div>

                {error   && <Alert className="bc-alert" variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
                {message && <Alert className="bc-alert" variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}

                <ImovelSelector onSelect={setImovelData} />

                {/* Seção 1 — Dados */}
                <div className="bc-section">
                    <div className="bc-section-title">
                        <span className="bc-section-num">1</span>
                        Dados do Imóvel
                    </div>

                    <form onSubmit={handleCalculate}>

                        <Row className="mb-3">
                            <Col md={6}>
                                <label className="bc-label">Tipo de Benfeitoria</label>
                                <select
                                    className="form-select bc-select"
                                    value={benfeitoriaType}
                                    onChange={(e) => setBenfeitoriaType(e.target.value)}
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {benfeitoriaTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Col>
                            <Col md={6}>
                                <label className="bc-label">Padrão do Imóvel</label>
                                <select
                                    className="form-select bc-select"
                                    value={propertyStandard}
                                    onChange={(e) => setPropertyStandard(e.target.value)}
                                    required
                                    disabled={!benfeitoriaType}
                                >
                                    <option value="">Selecione...</option>
                                    {propertyStandards.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <label className="bc-label">Estado de Conservação</label>
                            <select
                                className="form-select bc-select"
                                value={estadoConservacao}
                                onChange={(e) => setEstadoConservacao(e.target.value)}
                                required
                            >
                                <option value="">Selecione o estado de conservação...</option>
                                {estadosConservacao.map(e => (
                                    <option key={e.value} value={e.value}>{e.label}</option>
                                ))}
                            </select>
                        </div>

                        <Row className="mb-4">
                            <Col md={6}>
                                <label className="bc-label">Área (m²)</label>
                                <input
                                    type="number"
                                    className="form-control bc-input"
                                    placeholder="Ex: 150.50"
                                    step="0.01"
                                    value={area}
                                    onChange={(e) => setArea(e.target.value)}
                                    required
                                />
                            </Col>
                            <Col md={6}>
                                <label className="bc-label">Idade Aparente (anos)</label>
                                <input
                                    type="number"
                                    className="form-control bc-input"
                                    placeholder="Ex: 5"
                                    value={apparentAge}
                                    onChange={(e) => setApparentAge(e.target.value)}
                                    required
                                />
                            </Col>
                        </Row>

                        <button type="submit" className="btn bc-btn bc-btn-primary w-100" disabled={loading}>
                            {loading
                                ? <><Spinner animation="border" size="sm" className="me-2" />Calculando...</>
                                : 'Calcular'}
                        </button>
                    </form>
                </div>

                {/* Seção 2 — Resultados */}
                {calculationResult && (
                    <div className="bc-section">
                        <div className="bc-section-title">
                            <span className="bc-section-num">2</span>
                            Resultado do Cálculo
                        </div>

                        <div className="bc-results-grid">
                            <div className="bc-result-row">
                                <span className="label">R8N</span>
                                <span className="value">R$ {fmt(calculationResult.r8n_value)}</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Intervalo</span>
                                <span className="value">{fmt(calculationResult.intervalo, 3)}</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Vida Referencial</span>
                                <span className="value">{calculationResult.vida_referencial} anos</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Valor Residual</span>
                                <span className="value">{fmt(calculationResult.valor_residual * 100)}%</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Idade Referencial</span>
                                <span className="value">{fmt(calculationResult.idade_referencial)}%</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Estado</span>
                                <span className="value">{calculationResult.estado_conservacao} — {calculationResult.descricao_estado}</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Depreciação (d)</span>
                                <span className="value">{fmt(calculationResult.depreciacao_d)}%</span>
                            </div>
                            <div className="bc-result-row">
                                <span className="label">Foc</span>
                                <span className="value">{fmt(calculationResult.foc, 4)}</span>
                            </div>
                            <div className="bc-result-row" style={{ gridColumn: '1 / -1' }}>
                                <span className="label">VUB (R$/m²)</span>
                                <span className="value">R$ {fmt(calculationResult.vub)}</span>
                            </div>
                        </div>

                        <div className="bc-total-box">
                            {imovelData?.nome && <div className="imovel-tag">{imovelData.nome}</div>}
                            <div className="lbl">Valor Total Estimado</div>
                            <div className="val">R$ {fmt(calculationResult.valor_total)}</div>
                            <div className="sub">Método Ross-Heidecke</div>
                        </div>

                        <button
                            type="button"
                            className="btn bc-btn bc-btn-success w-100 mt-3"
                            onClick={handleSaveCalculation}
                            disabled={saving}
                        >
                            {saving
                                ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</>
                                : 'Salvar no Histórico'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CalculatorForm;
